import type { CellRange } from './types'

/** 1 → "A", 27 → "AA" */
export function colName(col: number): string {
  let n = col
  let s = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    s = String.fromCharCode(65 + rem) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

/** "A" → 1, "AA" → 27 */
export function colIndex(name: string): number {
  let n = 0
  const up = name.toUpperCase()
  for (let i = 0; i < up.length; i++) n = n * 26 + (up.charCodeAt(i) - 64)
  return n
}

export function ref(row: number, col: number): string {
  return colName(col) + row
}

const REF_RE = /^\$?([A-Za-z]{1,3})\$?([0-9]{1,7})$/

export function parseRef(s: string): { row: number; col: number } | null {
  const m = REF_RE.exec(s.trim())
  if (!m) return null
  return { row: Number(m[2]), col: colIndex(m[1]) }
}

/**
 * "A1", "A1:C10", "Sheet1!A1:C10", "A:C", "1:5" 를 해석한다.
 * 열/행 전체 지정은 maxRows/maxCols 로 클램프된다.
 */
export function parseRange(
  input: string,
  maxRows = 1000,
  maxCols = 64
): { sheet?: string; range: CellRange } | null {
  let s = input.trim()
  let sheet: string | undefined
  const bang = s.lastIndexOf('!')
  if (bang >= 0) {
    sheet = s.slice(0, bang).replace(/^'|'$/g, '').replace(/''/g, "'")
    s = s.slice(bang + 1)
  }
  const [aRaw, bRaw] = s.split(':')
  const a = (aRaw ?? '').trim()
  const b = (bRaw ?? '').trim()

  const norm = (r: CellRange): CellRange => ({
    r1: Math.min(r.r1, r.r2),
    c1: Math.min(r.c1, r.c2),
    r2: Math.max(r.r1, r.r2),
    c2: Math.max(r.c1, r.c2),
  })

  if (!b) {
    const p = parseRef(a)
    if (!p) return null
    return { sheet, range: { r1: p.row, c1: p.col, r2: p.row, c2: p.col } }
  }

  const pa = parseRef(a)
  const pb = parseRef(b)
  if (pa && pb) return { sheet, range: norm({ r1: pa.row, c1: pa.col, r2: pb.row, c2: pb.col }) }

  // "A:C" 전체 열
  if (/^\$?[A-Za-z]{1,3}$/.test(a) && /^\$?[A-Za-z]{1,3}$/.test(b)) {
    return {
      sheet,
      range: norm({ r1: 1, c1: colIndex(a.replace('$', '')), r2: maxRows, c2: colIndex(b.replace('$', '')) }),
    }
  }
  // "1:5" 전체 행
  if (/^\$?[0-9]+$/.test(a) && /^\$?[0-9]+$/.test(b)) {
    return { sheet, range: norm({ r1: Number(a.replace('$', '')), c1: 1, r2: Number(b.replace('$', '')), c2: maxCols }) }
  }
  return null
}

export function rangeToA1(r: CellRange): string {
  return r.r1 === r.r2 && r.c1 === r.c2 ? ref(r.r1, r.c1) : `${ref(r.r1, r.c1)}:${ref(r.r2, r.c2)}`
}

export function normalizeRange(r: CellRange): CellRange {
  return {
    r1: Math.min(r.r1, r.r2),
    c1: Math.min(r.c1, r.c2),
    r2: Math.max(r.r1, r.r2),
    c2: Math.max(r.c1, r.c2),
  }
}

export function forEachCell(r: CellRange, fn: (row: number, col: number) => void) {
  const n = normalizeRange(r)
  for (let row = n.r1; row <= n.r2; row++) for (let col = n.c1; col <= n.c2; col++) fn(row, col)
}

const REF_SCAN = /(?<![A-Za-z0-9_.$])(\$?)([A-Za-z]{1,3})(\$?)([0-9]{1,7})(?![A-Za-z0-9_(])/g

/** 수식을 문자열 리터럴 구간(홀수 인덱스)과 코드 구간(짝수 인덱스)으로 쪼갠다. */
function splitLiterals(formula: string): string[] {
  return formula.split(/("(?:[^"]|"")*")/)
}

/**
 * 셀을 이동/복사할 때 상대참조($ 없는 쪽)만 오프셋한다.
 * 정렬·행이동에서 Excel 과 동일한 동작.
 */
export function offsetFormula(formula: string, dRow: number, dCol: number): string {
  if (!dRow && !dCol) return formula
  const parts = splitLiterals(formula)
  for (let i = 0; i < parts.length; i += 2) {
    parts[i] = parts[i].replace(REF_SCAN, (_w, d1: string, cn: string, d2: string, rn: string) => {
      const col = d1 ? colIndex(cn) : colIndex(cn) + dCol
      const row = d2 ? Number(rn) : Number(rn) + dRow
      if (row < 1 || col < 1) return '#REF!'
      return `${d1}${colName(col)}${d2}${row}`
    })
  }
  return parts.join('')
}

/**
 * 행/열 삽입·삭제 시 수식 안의 A1 참조를 이동시킨다.
 * 문자열 리터럴 안은 건드리지 않으며, LOG10( 같은 함수명은 뒤의 '(' 로 걸러낸다.
 */
export function shiftFormula(
  formula: string,
  opts: { at: number; delta: number; axis: 'row' | 'col' }
): string {
  const { at, delta, axis } = opts
  // 문자열 리터럴 보존
  const parts = splitLiterals(formula)
  for (let i = 0; i < parts.length; i += 2) {
    parts[i] = parts[i].replace(
      REF_SCAN,
      (_w, d1: string, cn: string, d2: string, rn: string) => {
        let col = colIndex(cn)
        let row = Number(rn)
        if (axis === 'row') {
          if (row >= at) row += delta
          if (row < 1) return '#REF!'
        } else {
          if (col >= at) col += delta
          if (col < 1) return '#REF!'
        }
        return `${d1}${colName(col)}${d2}${row}`
      }
    )
  }
  return parts.join('')
}
